import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { UserPlus } from "lucide-react";
import { AnalyticsService, trackPageView } from "@/services/analytics";
import {
  GenerateButton,
  PromptInput,
  UpgradePlansModal,
  AuthModal,
  MobileBottomBar,
} from "@/components/ui";
import { PiecanNotice } from "@/components/features/piecan-notice";
import { useStartGeneration, useActiveGenerations } from "@/hooks";
import { useUserStore, hasAccessToPlanLevel } from "@/stores/user-store";

const IMAGE_CREDIT_COST = 5;

// Wan 2.2 realism endpoint enforces a fixed 864×1152 output
// server-side regardless of `aspect_ratio`. We still send the closest
// canonical ratio so anything downstream that reads it (DB params,
// success-page layout) sees a sensible value — but the actual image
// dimensions are decided by the provider, not by us.
const CHARACTER_ASPECT_RATIO = "3:4";
const CHARACTER_MODEL_ID = "wan_2_2_t2i_realism";

/**
 * Dedicated entry point for character creation. Prompt-only by design:
 * the prompt scaffold (full-body framing + default wardrobe) and the
 * auto-named character are both handled server-side when
 * `is_character_draft: true` is set on the request — the form stays a
 * single field, the user's prompt is sent verbatim, and the result
 * lands as an unsaved draft. The success page offers Save / Edit /
 * Try Again / Discard.
 */
export function CreateCharacterPage() {
  const { isSignedIn, user } = useUser();
  const { credits, plan } = useUserStore();
  const { startGeneration } = useStartGeneration();
  const { hasActiveGeneration } = useActiveGenerations();

  const [prompt, setPrompt] = useState("");
  // Once the user presses Generate we lock the button until the
  // navigation actually swaps the page out — prevents accidental
  // double-submission on slow clicks. Component unmounts on
  // navigate, so we never need to flip this back to false.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    preset: "no-credits" | "concurrent-limit";
    creditsNeeded?: number;
  }>({ open: false, preset: "no-credits" });
  const [showAuthModal, setShowAuthModal] = useState(false);

  const hasUnlimitedConcurrent = hasAccessToPlanLevel(plan, "pro");

  useEffect(() => {
    trackPageView("Create Character", "Create Character", user?.id);
  }, [user?.id]);

  const isValid = prompt.trim().length > 0 && !isSubmitting;

  const handleGenerate = () => {
    if (!isValid) return;

    if (!isSignedIn) {
      setShowAuthModal(true);
      return;
    }

    if (credits < IMAGE_CREDIT_COST) {
      AnalyticsService.trackOutOfCredits({
        credits_remaining: credits,
        credits_required: IMAGE_CREDIT_COST,
        model_attempted: CHARACTER_MODEL_ID,
        generation_type: "image",
        is_paid_user: plan !== "free",
        user_plan: plan,
      });
      setUpgradeModal({
        open: true,
        preset: "no-credits",
        creditsNeeded: IMAGE_CREDIT_COST,
      });
      return;
    }

    if (hasActiveGeneration && !hasUnlimitedConcurrent) {
      setUpgradeModal({ open: true, preset: "concurrent-limit" });
      return;
    }

    const userPrompt = prompt.trim();

    // Lock the button before we navigate — `navigate` is fast but
    // not synchronous from the user's perspective, and the click
    // handler can fire twice on a single press if the device
    // double-fires.
    setIsSubmitting(true);
    startGeneration({
      modelId: CHARACTER_MODEL_ID,
      prompt: userPrompt,
      type: "image",
      generateParams: {
        model_id: CHARACTER_MODEL_ID,
        prompt: userPrompt,
        aspect_ratio: CHARACTER_ASPECT_RATIO,
        // Optimizer always runs for character drafts. Backend forces
        // `style: "realistic"` so the rewrite matches the realism
        // endpoint's expected prose style.
        prompt_enhancer: true,
        // Backend reads this and (1) prepends the fixed character-
        // draft prefix on top of the optimized prompt before
        // submitting to the provider, and (2) persists
        // `is_character_draft: true` in `params` so the success UI
        // shows draft CTAs even after a page refresh.
        is_character_draft: true,
      },
      // No `folder` / `name` here on purpose: the generation lands as
      // a draft; the success page lets the user explicitly Save it,
      // at which point the server picks the next "Character N" name.
      isCharacterDraft: true,
    });
  };

  return (
    <>
      <div className="p-6 md:p-8 max-w-4xl mx-auto">
        <div className="space-y-5 pb-24 md:pb-0">
          {/* Partner upsell — points upload-capable users to PieCan for the
              image-upload flow. Compact so the prompt stays primary. */}
          <PiecanNotice variant="compact" source="character_page" />

          <PromptInput
            value={prompt}
            onChange={setPrompt}
            placeholder="Example: latina with curves, lip filler, beach background"
          />

          {/* Generate Button - Desktop */}
          <div className="hidden md:block">
            <GenerateButton
              disabled={!isValid}
              className="w-full"
              onClick={handleGenerate}
            >
              <UserPlus className="size-5" />
              Create Character ({IMAGE_CREDIT_COST} credits)
            </GenerateButton>
          </div>
        </div>
      </div>

      {/* Fixed bottom bar - Mobile only */}
      <MobileBottomBar>
        <GenerateButton
          disabled={!isValid}
          className="w-full"
          onClick={handleGenerate}
        >
          <UserPlus className="size-5" />
          Create Character ({IMAGE_CREDIT_COST} credits)
        </GenerateButton>
      </MobileBottomBar>

      <UpgradePlansModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, preset: "no-credits" })}
        preset={upgradeModal.preset}
        presetContext={{
          creditsNeeded: upgradeModal.creditsNeeded,
          currentCredits: credits,
        }}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
